
import Vue from "vue";
import ChatMessageBubble from "../components/ChatMessage.vue";
import ChatComposer from "../components/ChatComposer.vue";
import FollowupChips from "../components/FollowupChips.vue";
import { ChatMessage } from "../models/ChatMessage";
import { AskCenAiPrompt } from "../models/AskCenAiPrompt";

const ICON_HELP = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6"/><path d="M9.6 9.3a2.5 2.5 0 0 1 4.9.7c0 1.7-2.5 2-2.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M12 17h.01" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
const ICON_ALERT = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 9v4M12 16.5h.01M10.29 3.86 1.82 18a1.5 1.5 0 0 0 1.3 2.25h17.76a1.5 1.5 0 0 0 1.3-2.25L13.71 3.86a1.5 1.5 0 0 0-2.42 0Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_BATTERY = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="7" width="16" height="10" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M21 10v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M5 10v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;

const DEFAULT_PROMPTS: AskCenAiPrompt[] = [
  {
    label: "Getting started",
    text: "What can you help me with, and what data can you see?",
    icon: ICON_HELP,
  },
  {
    label: "Active alerts overview",
    text: "Give me an overview of all active alerts across the platform and which ones need attention first.",
    icon: ICON_ALERT,
  },
  {
    label: "Sensor health check",
    text:
      "Which sensors are offline, reporting no data, or low on battery right now? " +
      "Group them by plant and tell me which sites need attention.",
    icon: ICON_BATTERY,
  },
];

let msgIdCounter = 0;
function nextId(): string {
  return `msg-${++msgIdCounter}`;
}

export default Vue.extend({
  name: "AskCenAiPage",
  components: { ChatMessageBubble, ChatComposer, FollowupChips },
  data() {
    return {
      messages: [] as ChatMessage[],
      sessionId: null as string | null,
      isWaiting: false,
      scopeCompanyId: null as number | null,
      companies: [] as any[],
      followupPrompts: DEFAULT_PROMPTS as AskCenAiPrompt[],
      prefillText: "",
      showLeaveDialog: false,
      leaveResolver: null as ((ok: boolean) => void) | null,
    };
  },
  async created() {
    try {
      const result = await (this as any).$dep.companyUseCase.getCompanies();
      this.companies = Array.isArray(result) ? result : result?.data ?? [];
    } catch {
      // non-fatal: company list stays empty, user can still chat with Platform scope
    }
  },
  methods: {
    scopeLabel(): string {
      if (this.scopeCompanyId === null) return "Platform";
      const c = this.companies.find((c: any) => c.id === this.scopeCompanyId);
      return c ? c.title || c.name : "Platform";
    },

    handleChipSelect(text: string) {
      this.prefillText = text;
    },

    async handleSend(text: string) {
      const company = this.scopeLabel();

      const userMsgId = nextId();
      this.messages.push({
        id: userMsgId,
        role: "user",
        content: text,
        company,
        timestamp: new Date(),
      });

      this.isWaiting = true;
      this.$nextTick(() => this.scrollToBottom());

      try {
        const res = await (this as any).$dep.askCenAiUseCase.chat(text, this.sessionId, this.scopeCompanyId);

        if (res.session_id && !this.sessionId) {
          this.sessionId = res.session_id;
        }

        this.messages.push({
          id: nextId(),
          role: "bot",
          content: res.answer || "",
          company,
          timestamp: new Date(),
        });

        if (Array.isArray(res.follow_up_questions) && res.follow_up_questions.length) {
          this.followupPrompts = res.follow_up_questions.map((q: string) => ({
            label: q,
            text: q,
          }));
        } else {
          this.followupPrompts = DEFAULT_PROMPTS;
        }
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        const content = typeof detail === "string" && detail ? detail : "Sorry, something went wrong. Please try again.";
        this.messages.push({
          id: nextId(),
          role: "bot",
          content,
          company,
          timestamp: new Date(),
        });
        this.followupPrompts = DEFAULT_PROMPTS;
      } finally {
        this.isWaiting = false;
        this.$nextTick(() => this.scrollQuestionToTop(userMsgId));
      }
    },

    clearChat() {
      this.messages = [];
      this.sessionId = null;
      this.followupPrompts = DEFAULT_PROMPTS;
    },

    scrollToBottom() {
      const el = this.$refs.chatWrap as HTMLElement;
      if (el) el.scrollTop = el.scrollHeight;
    },

    scrollQuestionToTop(id: string) {
      const wrap = this.$refs.chatWrap as HTMLElement;
      const comp = this.$refs["msg-" + id] as any;
      const el = (Array.isArray(comp) ? comp[0] : comp)?.$el as HTMLElement;
      if (wrap && el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },

    confirmLeave(): Promise<boolean> {
      this.showLeaveDialog = true;
      return new Promise((resolve) => {
        this.leaveResolver = resolve;
      });
    },

    // Close the dialog before resolving so Vuetify can tear down its scrim
    // and scroll lock while the component is still alive.
    resolveLeave(ok: boolean) {
      this.showLeaveDialog = false;
      const resolve = this.leaveResolver;
      this.leaveResolver = null;
      if (resolve) resolve(ok);
    },

    onLeaveCancel() {
      this.resolveLeave(false);
    },

    onLeaveConfirm() {
      this.resolveLeave(true);
    },
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.messages.length) {
      next();
      return;
    }
    const isConfirmed = await (this as any).confirmLeave();
    await this.$nextTick();
    if (isConfirmed) {
      next();
    } else {
      next(false);
    }
  },
});
